import errorReporter from '@app/errorReporter';
import { Color, Flex, List, Text } from '@bb/elements-web';
import type { CurrentUser } from '@/app-package';
import React from 'react';
import styled from 'styled-components';
import { AuthContextUser } from 'contexts/types';
import searchUsers from '../../../../api/UserController/search';
import SelectAgentInput from '../components/SelectAgentInput';

// Copied from AgentRosterInput from assets/app/react-common
// but removed things not applicable to virtual showings
// and changed the render to not rely on angular styles (which in turn required some logical changes)

// This can be further divided into the 2 agent types: Roster agent and BrokerBay agent
export type AgentUserResult = {
  id?: string;
  score: number;
  firstName: string;
  lastName: string;
  orgName: string;
  addressName: string;
  email: string;
  fullName: string;
  uid?: string;
  boardId?: string;
  mlsType?: string;
};

type VirtualRosterInputState = {
  loading: boolean;
  /** debounce variable */
  cancelSearch: boolean;
  /** Search results from using the enter key (results in a pagination render state) */
  searchAgentUserResults?: AgentUserResult[];
  searchAgentUserResultsCount?: number;
  /** Search results from typing in input */
  searchUserPopups: AgentUserResult[];
  agentSuggestName: string;
  page: number;
  /** If arrow keys are used to navigate dropdown, then disable enter key functionality */
  usedArrowKeys: boolean;
};

type VirtualRosterInputProps = {
  selectUser: (item: AgentUserResult) => void;
  deselectUser: () => void;
  auth: AuthContextUser;
  listingId: string;
};

class VirtualRosterInput extends React.Component<
  VirtualRosterInputProps,
  VirtualRosterInputState
> {
  constructor(props: VirtualRosterInputProps) {
    super(props);
    this.state = {
      loading: false,
      cancelSearch: false,
      searchAgentUserResults: undefined,
      searchUserPopups: [],
      agentSuggestName: '',
      page: 1,
      searchAgentUserResultsCount: 0,
      usedArrowKeys: false,
    };
  }

  _handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const { agentSuggestName, usedArrowKeys } = this.state;
    const { listingId } = this.props;
    if (event.key === 'Enter') {
      if (!agentSuggestName || usedArrowKeys) return;
      this.search(agentSuggestName, listingId);
    } else if (['Escape', 'Esc'].includes(event.key)) {
      this.setState({ searchUserPopups: [], usedArrowKeys: false });
    } else if (['ArrowUp', 'Up', 'ArrowDown', 'Down'].includes(event.key)) {
      this.setState({ usedArrowKeys: true });
    }
  };

  public select = (user: AgentUserResult) => {
    const { selectUser } = this.props;
    this.setState(
      {
        searchAgentUserResults: undefined,
        usedArrowKeys: false,
        cancelSearch: false,
      },
      () => selectUser(user),
    );
  };

  public fetchUsers = async ({
    name,
    listingId,
    page,
  }: {
    name: string;
    listingId: string;
    page?: number;
  }) => {
    const { users, count } = await searchUsers({
      name,
      listingId,
      excludeInternalOrgs: false,
      // Note: Before this was copied from AgentRosterInput custom agents were included
      // However this would lead to the page loading forever so they have been excluded here now
      includeCustomAgents: false,
      ...(page && { page }),
    });
    return { users, count };
  };

  public search = async (name: string, listingId: string) => {
    try {
      const { deselectUser } = this.props;
      const { page } = this.state;
      this.setState({
        loading: true,
        cancelSearch: true,
        searchUserPopups: [],
      });
      deselectUser();

      const { users, count } = await this.fetchUsers({ name, page, listingId });

      this.setState({
        searchAgentUserResultsCount: count,
        searchAgentUserResults: users.sort((a, b) => b.score - a.score),
        cancelSearch: false,
      });
    } catch (err) {
      errorReporter.error(`Error searching roster: ${err}`);
    } finally {
      this.setState({ loading: false });
    }
  };

  public agentSuggest = async (name: string) => {
    const { cancelSearch, agentSuggestName } = this.state;
    const { listingId } = this.props;

    if (agentSuggestName !== name) return;
    if (!name) {
      this.setState({ searchUserPopups: [] });
      return;
    }
    if (cancelSearch) {
      this.setState({ searchUserPopups: [], cancelSearch: false });
      return;
    }
    try {
      const { users } = await this.fetchUsers({ name, listingId });
      const searchUserPopups = users
        .sort((a, b) => b.score - a.score)
        .slice(0, 10);
      this.setState({ searchUserPopups });
    } catch (err) {
      errorReporter.error(`Error searching roster: ${err}`);
    } finally {
      this.setState({ cancelSearch: false });
    }
  };

  public onSearchChange = async (agentSuggestName: string) => {
    this.setState(
      {
        agentSuggestName,
        page: 1,
        searchAgentUserResults: undefined,
        searchAgentUserResultsCount: 0,
      },
      () => {
        setTimeout(() => {
          this.agentSuggest(agentSuggestName);
        }, 300);
      },
    );
  };

  public onAgentSelect = (userIndex: number) => {
    const user = this.state.searchUserPopups[userIndex];
    this.setState({ cancelSearch: true, searchUserPopups: [] }, () =>
      this.select(user),
    );
  };

  public onPageChange = (page: number) => {
    const { agentSuggestName } = this.state;
    const { listingId } = this.props;
    this.setState({ page }, () => this.search(agentSuggestName, listingId));
  };

  render() {
    const {
      agentSuggestName,
      searchAgentUserResults,
      searchUserPopups,
      page,
      searchAgentUserResultsCount,
      loading,
    } = this.state;

    return (
      <StyledFlex vertical gap="small">
        <Text thickness="bold">Agent Name</Text>
        <SelectAgentInput
          searchValue={agentSuggestName}
          onKeyDown={this._handleKeyDown}
          onChange={this.onAgentSelect}
          onSearch={this.onSearchChange}
          id="agent-roster-input"
          dataCy="agent-roster-input"
          options={searchUserPopups.map((user, index) => ({
            value: index,
            label: (
              <Flex
                vertical
                data-cy={user.id || user.uid}
                key={user.id || `${user.uid}-${user.boardId}`}
              >
                <Text thickness="bold" dataCy={`${user.id || user.uid}-name`}>
                  {user.firstName} {user.lastName}
                </Text>
                <Text dataCy={`${user.id || user.uid}-address`}>
                  {user.addressName}
                </Text>
              </Flex>
            ),
          }))}
        />

        {searchAgentUserResults && (
          <List
            dataCy="virtualRoster-list"
            dataSource={searchAgentUserResults}
            locale={{ emptyText: 'No users found' }}
            pagination={{
              position: 'bottom',
              align: 'center',
              itemRender: (pageNumber, type, element) => {
                if (type !== 'page') return element;
                // Don't show outer bound page numbers
                return Math.abs(page - pageNumber) > 10 ? null : element;
              },
              total: searchAgentUserResultsCount,
              onChange: this.onPageChange,
              showSizeChanger: false,
              size: 'small',
              disabled: loading,
              hideOnSinglePage: true,
            }}
            renderItem={user => (
              <StyledListItem
                vertical
                key={user.id || `${user.uid}-${user.boardId}`}
                data-cy={user.id || user.uid}
                onClick={() => this.select(user)}
              >
                <Text thickness="bold" dataCy={`${user.id || user.uid}-name`}>
                  {user.firstName} {user.lastName}
                </Text>
                <Text dataCy={`${user.id || user.uid}-address`}>
                  {user.addressName}
                </Text>
              </StyledListItem>
            )}
          />
        )}
      </StyledFlex>
    );
  }
}

const StyledFlex = styled(Flex)`
  margin-top: 16px;
`;

const StyledListItem = styled(Flex)`
  cursor: pointer;
  padding: 4px 16px;
  &:hover {
    background-color: ${Color.LIGHTER_GREY};
  }
`;

export default VirtualRosterInput;
